import { createFileRoute, Link } from '@tanstack/react-router'
import { Printer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from '@/api/dispatches'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { Button } from '@/components/ui/button'

// The print-friendly note of a Dispatch (UI-SCREENS I-06, G24, owner decision 2026-09-23 on
// #171 Q6): a plain HTML page styled for the browser's print dialog — Workspace, channel, date,
// Dispatch number, SKU, name, quantity and the total units. **No prices** (G24). The shell's
// sidebar and header carry `print:hidden`, so only this page ends up on paper; the buttons here
// do too.
export const Route = createFileRoute('/w/$slug/dispatches/$dispatchId/print')({
  component: DispatchPrintPage,
  staticData: { breadcrumb: 'dispatches.print.title' },
})

function DispatchPrintPage() {
  const { t } = useTranslation()
  const { slug, dispatchId } = Route.useParams()
  const id = Number(dispatchId)
  const dispatch = useDispatch(slug, id)
  const workspace = useWorkspace(slug)

  if (dispatch.isPending) return <LoadingSkeleton variant="page" />
  if (dispatch.isError || !dispatch.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data = dispatch.data

  return (
    <section className="flex max-w-3xl flex-col gap-6 print:max-w-none print:gap-4 print:text-black">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Link to="/w/$slug/dispatches/$dispatchId" params={{ slug, dispatchId }} className="text-sm underline-offset-4 hover:underline">
          {t('dispatches.print.back')}
        </Link>
        <Button type="button" className="ml-auto h-11" onClick={() => window.print()}>
          <Printer aria-hidden="true" />
          {t('dispatches.print.print')}
        </Button>
      </div>

      <header className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-semibold">{t('dispatches.print.title')}</h1>
        <p className="text-lg font-medium">{data.dispatch_no}</p>
        <p className="text-sm">{workspace.data?.name ?? ''}</p>
      </header>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Figure label={t('dispatches.columns.channel')} value={data.sales_channel.name} />
        <Figure label={t('dispatches.columns.date')} value={formatBusinessDate(data.occurred_on)} />
      </dl>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-2 pr-4 font-medium">{t('dispatches.print.sku')}</th>
            <th className="py-2 pr-4 font-medium">{t('dispatches.print.name')}</th>
            <th className="py-2 text-right font-medium">{t('dispatches.print.qty')}</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((line) => (
            <tr key={line.id} className="border-b">
              <td className="py-2 pr-4">{line.variant.sku}</td>
              <td className="py-2 pr-4">
                {line.variant.product_name} · {line.variant.name}
              </td>
              <td className="py-2 text-right">{line.qty}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="py-2 pr-4 font-medium" colSpan={2}>
              {t('dispatches.print.total')}
            </td>
            <td className="py-2 text-right font-medium">{data.total_units}</td>
          </tr>
        </tfoot>
      </table>

      {data.note ? <p className="text-sm">{data.note}</p> : null}
      {data.voided_at ? <p className="text-sm font-medium">{t('corrections.voided')}</p> : null}
    </section>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
